import { MenuItem, Select } from "@mui/material";

import { SkillVersionRecord } from "@/shared/domain/skills";
import { HistoryIcon } from "@/shared/foundation/icons/mui";

import styles from "./SkillVersionSelect.module.css";

interface Props {
  versions: SkillVersionRecord[];
  /** Version currently on screen. */
  selected: number | null;
  latest: number | null;
  /**
   * Frozen mid-edit: switching version under an open form would retarget the
   * pending save at a body the author never opened.
   */
  disabled: boolean;
  onChange: (version: number) => void;
}

/**
 * The skill's version history, collapsed into one control.
 *
 * This replaced a fixed 240px rail listing every version down the left of the
 * page. With the two or three versions a skill typically has, the rail was
 * mostly empty space competing with the procedure — the thing people actually
 * come here to read. Per-version descriptions went with it; the version number
 * and the `latest` marker are what distinguish entries in a dropdown.
 */
export function SkillVersionSelect({
  versions,
  selected,
  latest,
  disabled,
  onChange,
}: Props) {
  if (versions.length === 0) {
    return null;
  }
  const ordered = [...versions].sort((a, b) => b.version - a.version);
  return (
    <Select
      size="small"
      value={selected ?? ""}
      disabled={disabled}
      className={styles.select}
      onChange={(e) => onChange(Number(e.target.value))}
      inputProps={{ "aria-label": "Version" }}
      // The clock marks this as history rather than as one more setting — the
      // control is otherwise indistinguishable from the accessibility select
      // a few pixels away.
      startAdornment={<HistoryIcon className={styles.icon} fontSize="small" />}
      renderValue={(value) => (
        <span className={styles.value}>
          <span className={styles.version}>v{String(value)}</span>
          {Number(value) === latest && (
            <span className={styles.latest}>· latest</span>
          )}
        </span>
      )}
    >
      {ordered.map((version) => (
        <MenuItem key={version.version} value={version.version}>
          <span className={styles.version}>v{version.version}</span>
          {version.version === latest && (
            <span className={styles.latest}>latest</span>
          )}
        </MenuItem>
      ))}
    </Select>
  );
}
